import { useEffect, useState } from "react";
import { Bell, RefreshCw, Send, Loader2, Film, CalendarDays, Radio, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn, Loading, Empty, handleErr } from "./AdminUI";

const TYPE_META = {
  vod: { label: "New VOD", icon: Film, color: "text-[#E11D2A]" },
  event: { label: "Event", icon: CalendarDays, color: "text-amber-400" },
  program: { label: "Program", icon: Radio, color: "text-[#2563EB]" },
  reminder: { label: "5-min reminder", icon: Bell, color: "text-emerald-400" },
  promo: { label: "Promotion", icon: Megaphone, color: "text-fuchsia-400" },
};
const AUD = { all: "All members", subscribers: "Subscribers", basic: "Basic", premium: "Premium" };
const Stat = ({ label, value, color }) => (
  <div className="text-center px-2"><div className={`text-lg font-extrabold ${color || "text-white"}`}>{value ?? 0}</div><div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div></div>
);

export default function AdminNotifications() {
  const [items, setItems] = useState(null);
  const [resending, setResending] = useState("");

  const load = () => endpoints.notifHistory(150).then((r) => setItems(r.data.notifications || [])).catch(handleErr);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const resend = async (n) => {
    if (!window.confirm(`Send this notification again to ${AUD[n.audience] || n.audience}?`)) return;
    setResending(n.id);
    try { await endpoints.notifResend(n.id); toast.success("Re-sending notification"); load(); }
    catch (e) { handleErr(e); } finally { setResending(""); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  if (items === null) return <Loading />;

  return (
    <div data-testid="admin-notifications-page">
      <PageHeader title="Notification History" desc="Every member broadcast and automatic reminder, with delivery stats.">
        <Btn variant="ghost" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Btn>
      </PageHeader>

      {items.length === 0 ? <Empty text="No notifications sent yet." /> : (
        <div className="space-y-3">
          {items.map((n) => {
            const meta = TYPE_META[n.type] || TYPE_META.promo;
            const Icon = meta.icon;
            const st = n.stats || {};
            return (
              <Card key={n.id} className="p-4" data-testid={`notif-row-${n.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-white/5 border border-[#273042] flex items-center justify-center ${meta.color}`}><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold truncate">{n.contentTitle || n.title}</div>
                    <div className="text-xs text-slate-400 flex flex-wrap items-center gap-x-2">
                      <span className={meta.color}>{meta.label}</span> · <span>{AUD[n.audience] || n.audience}</span> ·
                      <span>{(n.channels || []).join(", ").toUpperCase()}</span> · <span>{fmt(n.createdAt)}</span>
                      {n.auto && <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 text-[9px] font-bold uppercase">Auto</span>}
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${n.status === "done" ? "bg-slate-700 text-slate-300" : "bg-amber-500/20 text-amber-400"}`}>{n.status}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 rounded-xl bg-[#0A0C10] border border-[#273042] py-1.5">
                    <Stat label="Recipients" value={st.recipients} />
                    <Stat label="Sent" value={st.sent} color="text-[#2563EB]" />
                    <Stat label="Delivered" value={st.delivered} color="text-emerald-400" />
                    <Stat label="Failed" value={st.failed} color="text-red-400" />
                    <Stat label="Pending" value={st.pending} color="text-amber-400" />
                  </div>
                  {!n.auto && (
                    <Btn variant="ghost" onClick={() => resend(n)} disabled={resending === n.id} data-testid={`notif-resend-${n.id}`}>
                      {resending === n.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send again
                    </Btn>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
